import { Button, ButtonGroup, CircularProgress, InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import type { Account, Category, Member, Transaction, TransactionType } from "../../lib/types";
import { LinkedTransactionGroup, TransactionRow } from "../../components/TransactionRow";

export type MonthlyOperationType = Exclude<TransactionType, "correction"> | "";

export type MonthlyOperationsPreviewProps = {
  transactions: Transaction[];
  accounts: Account[];
  categories: Category[];
  members: Member[];
  selectedCategoryName?: string;
  type: MonthlyOperationType;
  search: string;
  loading: boolean;
  error: boolean;
  hasMore: boolean;
  onTypeChange: (type: MonthlyOperationType) => void;
  onSearchChange: (search: string) => void;
  onOpenTransaction: (transaction: Transaction) => void;
  onOpenTransactionId: (transactionId: string) => void;
  onOpenAll: () => void;
  onRetry: () => void;
};

export function MonthlyOperationsPreview({ transactions, accounts, categories, members, selectedCategoryName, type, search, loading, error, hasMore, onTypeChange, onSearchChange, onOpenTransaction, onOpenTransactionId, onOpenAll, onRetry }: MonthlyOperationsPreviewProps) {
  const typeButton = (value: MonthlyOperationType, label: string) => <Button type="button" variant={type === value ? "contained" : "outlined"} disableElevation onClick={() => onTypeChange(value)}>{label}</Button>;
  const transactionsById = new Map(transactions.map((transaction) => [transaction.id, transaction]));
  const visibleTransactions = transactions.filter((transaction) => {
    if (type === "expense" || type === "income") return transaction.type === type && !transaction.linked_transaction_id;
    if (type === "transfer") return transaction.type === "transfer" || Boolean(transaction.linked_transaction_id);
    return true;
  });
  const seenLinkedIds = new Set<string>();
  return (
    <section className="monthly-operations" aria-labelledby="monthlyOperationsTitle">
      <div className="monthly-operations-heading">
        <div><h2 id="monthlyOperationsTitle">{selectedCategoryName || "All operations"}</h2><p>{selectedCategoryName ? `Operations in the selected ${type === "income" ? "income" : "expense"} category` : "Income, expenses, and transfers this month"}</p></div>
        <div className="monthly-operations-heading-actions">
          {loading ? <CircularProgress size={20} aria-label="Updating operations" /> : null}
          {visibleTransactions.length ? <Button type="button" size="small" className="monthly-open-records" variant="outlined" onClick={onOpenAll}>{hasMore ? "Open all in Records" : "Open in Records"}</Button> : null}
        </div>
      </div>
      <TextField size="small" fullWidth placeholder="Search monthly operations" value={search} onChange={(event) => onSearchChange(event.target.value)} slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }} />
      <ButtonGroup className="monthly-operation-types" size="small" aria-label="Operation type">
        {typeButton("", "All")}{typeButton("expense", "Expenses")}{typeButton("income", "Income")}{typeButton("transfer", "Transfers")}
      </ButtonGroup>
      {error ? <div className="monthly-operations-state"><p>Could not load operations.</p><Button type="button" size="small" onClick={onRetry}>Retry</Button></div> : null}
      {!error && !loading && !visibleTransactions.length ? <p className="status stat-empty-note">No operations match these filters.</p> : null}
      <div className="monthly-operation-list">
        {visibleTransactions.map((transaction) => {
          if (!transaction.linked_transaction_id) return <TransactionRow key={transaction.id} transaction={transaction} accounts={accounts} categories={categories} members={members} onClick={() => onOpenTransaction(transaction)} />;
          if (seenLinkedIds.has(transaction.id) || seenLinkedIds.has(transaction.linked_transaction_id)) return null;
          seenLinkedIds.add(transaction.id);
          seenLinkedIds.add(transaction.linked_transaction_id);
          return <LinkedTransactionGroup key={[transaction.id, transaction.linked_transaction_id].sort().join(":")} primary={transaction} partner={transactionsById.get(transaction.linked_transaction_id) ?? transaction.linked_transaction ?? null} accounts={accounts} categories={categories} members={members} onOpen={onOpenTransaction} onOpenId={onOpenTransactionId} />;
        })}
      </div>
    </section>
  );
}
